import signinFormStyles from "./signinForms.module.css";
import React, { useEffect, useState } from "react";
import { ISigninFormProps } from "../../../types/signinTypes";
import { useNavigate } from "react-router-dom";
import { passwordCheck } from "../../../utils/utils";
import Modal from "../../modals/modal/Modal";
import SignupSuccessPopup from "../../modals/signup_success_popup/signup_success_popup";
import {
  useRegisterMutation,
  useSetUserPasswordQuery,
  useUpdateUserMutation,
} from "../../../services/api/auth.api";
import { useAppSelector } from "../../../services/hooks";
import { errorUserSelector } from "../../../services/selectors/auth.selectors";
import { useFormik } from "formik";
import { passwordValidationSchema } from "../../../utils/validation";

function CreatePasswordForm({ state }: ISigninFormProps) {
  const navigate = useNavigate();

  const [openModal, setOpenModal] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [repeatPasswordVisible, setRepeatPasswordVisible] = useState(false);
  const [submitPassword, setSubmitPassword] = useState(false);

  const signInError = useAppSelector(errorUserSelector);

  const [update] = useUpdateUserMutation();
  const [register, { isError, isLoading }] = useRegisterMutation();

  const submitForm = async () => {
    if (state.id === 0) {
      const response = await register({
        ...state,
        password: formik.values.password,
      });
      if ("data" in response && response.data.success) {
        setOpenModal(true);
      }
    } else {
      update({ ...state, password: formik.values.password }).then((res) => {
        if ("data" in res) {
          setSubmitPassword(true);
        }
      });
    }
  };

  const formik = useFormik({
    initialValues: { password: "", repeatPassword: "" },
    validationSchema: passwordValidationSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: submitForm,
  });

  const { isSuccess } = useSetUserPasswordQuery(
    {
      UsersGuid: state.guid,
      password: formik.values.password,
      roleId: state.roleId,
    },
    {
      skip: !submitPassword, // Skip the query until explicitly called
    }
  );
  const togglePasswordVisibility = () => {
    setPasswordVisible((prev) => !prev);
  };

  const toggleRepeatPasswordVisibility = () => {
    setRepeatPasswordVisible((prev) => !prev);
  };

  const closeModal = () => {
    navigate("/prevencher", {
      state: {
        pathname: "chatSignin",
      },
    });
    setOpenModal(false);
  };

  useEffect(() => {
    if (isSuccess && submitPassword) navigate("/login");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, submitPassword]);

  return (
    <form
      className={signinFormStyles.signin_private__form}
      onSubmit={formik.handleSubmit}
    >
      <label
        htmlFor="password"
        className={signinFormStyles.signin_private__label}
      >
        סיסמה חדשה
      </label>
      <div className={signinFormStyles.signin_private__inputContainer}>
        <input
          type={passwordVisible ? "text" : "password"}
          className={`${signinFormStyles.signin_private__input} ${
            signinFormStyles.signin_private__input_type_password
          } ${
            formik.errors.password &&
            signinFormStyles.signin_private__input_invalid
          }`}
          name="password"
          value={formik.values.password}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          required
          minLength={6}
        />
        <button
          type="button"
          className={`${signinFormStyles.signin_private__passwordToggle} ${
            passwordVisible &&
            signinFormStyles.signin_private__passwordToggle_visible
          }`}
          onClick={togglePasswordVisibility}
        />
      </div>
      {formik.touched.password && formik.errors.password && (
        <p
          className={`${signinFormStyles.signin_private__invalid_message} ${signinFormStyles.signin_private__invalid_message_visible}`}
        >
          {formik.errors.password}
        </p>
      )}
      <label
        htmlFor="repeatPassword"
        className={signinFormStyles.signin_private__label}
      >
        הקלד את הסיסמה שוב
      </label>
      <div className={signinFormStyles.signin_private__inputContainer}>
        <input
          type={repeatPasswordVisible ? "text" : "password"}
          className={`${signinFormStyles.signin_private__input} ${
            signinFormStyles.signin_private__input_type_password
          } ${
            formik.touched.repeatPassword &&
            formik.errors.repeatPassword &&
            signinFormStyles.signin_private__input_invalid
          }`}
          name="repeatPassword"
          id="repeatPassword"
          value={formik.values.repeatPassword}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          required
          minLength={6}
        />
        <button
          type="button"
          className={`${signinFormStyles.signin_private__passwordToggle} ${
            repeatPasswordVisible &&
            signinFormStyles.signin_private__passwordToggle_visible
          }`}
          onClick={toggleRepeatPasswordVisibility}
        />
      </div>
      {formik.touched.repeatPassword && formik.errors.repeatPassword && (
        <p
          className={`${signinFormStyles.signin_private__invalid_message} ${signinFormStyles.signin_private__invalid_message_visible}`}
        >
          {formik.errors.repeatPassword}
        </p>
      )}
      {formik.values.password && (
        <>
          <div className={signinFormStyles.signin_private__strongContainer}>
            {[1, 2, 3].map((level, index) => (
              <span
                key={index}
                className={`${signinFormStyles.signin_private__strong} ${
                  index < passwordCheck(formik.values.password).strength &&
                  signinFormStyles.signin_private__strong_active
                }`}
              />
            ))}
          </div>
          <p className={signinFormStyles.signin_private__strongText}>
            חוזק סיסמא: {passwordCheck(state.password).val}
          </p>
        </>
      )}
      <p className={signinFormStyles.signin_private__note}>הגדרת סיסמה חזקה:</p>
      <p className={signinFormStyles.signin_private__note}>
        באורך של לפחות 6 תווים אבל עדיף 8 או יותר.
      </p>
      <p className={signinFormStyles.signin_private__note}>
        שילוב של אותיות, מספרים וסמלים
      </p>
      {isError && (
        <p
          className={`${signinFormStyles.signin_private__invalid_message} ${signinFormStyles.signin_private__invalid_message_visible}`}
        >
          {signInError}
        </p>
      )}
      <button
        type="submit"
        disabled={!formik.isValid || !formik.dirty}
        className={`${signinFormStyles.signin_private__btn} ${
          !formik.isValid || !formik.dirty
            ? signinFormStyles.signin_private__btn_disabled
            : "hover"
        }`}
      >
        {isLoading ? "שליחה" : "המשך"}
      </button>
      {openModal && (
        <Modal onClose={closeModal}>
          <SignupSuccessPopup onClose={closeModal} />
        </Modal>
      )}
    </form>
  );
}

export default CreatePasswordForm;
